import { GetServerSidePropsContext } from 'next';

import { getRutilusGraphqlClient } from '../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusSSR } from '../../src/api/graphql/queryRutilusSSR';
import { AppLogic } from '../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../src/components/Layouts/GlobalLayout';
import { reportMykissClientError } from '../../src/helpers/errorHandling/client';
import { redirectNonExistingFishingWater } from '../../src/helpers/redirects/redirectNonExistingFishingWater';
import { isExternalId } from '../../src/helpers/strings/isExternalId';
import { GetFishingWater, GetFishingWaterVariables } from '../../src/interfaces/graphql';
import { SSRResult } from '../../src/interfaces/nextjs';
import { getFishingWater } from '../../src/queries/rutilus/FishingWaters';
import { PAGE_METADATA } from '../../src/views/SuggestWaterChanges/constants/constants';
import {
  ISuggestWaterChanges,
  SuggestWaterChanges,
} from '../../src/views/SuggestWaterChanges/SuggestWaterChanges';

export async function getServerSideProps({
  query,
}: GetServerSidePropsContext): SSRResult<ISuggestWaterChanges> {
  // Check if query object is empty
  if (Object.keys(query).length === 0) {
    return {
      props: {
        ...PAGE_METADATA,
        isNewForm: true,
      },
    };
  }

  const waterId = query.waterId as string;

  // For existing water, we need waterId
  if (!waterId || !isExternalId(waterId)) {
    return {
      notFound: true,
    };
  }

  try {
    const client = getRutilusGraphqlClient({ serverMode: 'ssr' });
    const { response } = await queryRutilusSSR<GetFishingWater, GetFishingWaterVariables>(client, {
      query: getFishingWater,
      variables: {
        externalId: waterId,
      },
    });

    if (!response) {
      return redirectNonExistingFishingWater(waterId);
    }

    const { displayName, longitude, latitude, metadata, url } = response.data.fishingWater;

    return {
      props: {
        ...PAGE_METADATA,
        fishingWater: {
          externalId: waterId,
          name: displayName,
          coordinates: {
            longitude,
            latitude,
          },
          metadata: metadata.map(metaItem => {
            return { groupId: metaItem.group.id, id: metaItem.id };
          }),
          url: url ?? '',
        },
        isNewForm: false,
      },
    };
  } catch (error) {
    reportMykissClientError(error);
    return {
      notFound: true,
    };
  }
}

SuggestWaterChanges.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout hideStickyBannerGetApp>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default SuggestWaterChanges;
